/* eslint-disable react/display-name */
import { useTranslation } from 'next-i18next';

import { memo, useCallback, useMemo, useRef, useState } from 'react';

import {
    Box,
    Button,
    Drawer,
    HStack,
    IconButton,
    Link,
    Portal,
    useBreakpointValue,
    VStack,
} from '@chakra-ui/react';

import { User } from '@nocowanie/noc-api';

import { urlHelpers } from '@nocowanie/core/helpers';
import { IconsLinear } from '@nocowanie/icons/linear';

import { AccordionMenuItem } from '@app/components/molecules/accordion-menu-item';
import { getDashboardLink, logOutUser, modifyLink } from '@app/helpers/navigation.helpers';
import { NavGroupModel, NavLinkModel } from '@app/models';

import navLinks from './navigation-links.json';

import staticData from '../../../assets/data/static-data.json';

const MenuFooter = memo((): JSX.Element => {
    const { t } = useTranslation('common', {
        keyPrefix: 'appHeader',
    });

    return (
        <Box p={0}>
            <Box color={'gray.500'} textTransform={'uppercase'} py={4} fontWeight={'bold'}>
                {t('footerGroupHeader')}
            </Box>
            <Box asChild>
                <a href={`mailto:${staticData.navigation.customerServiceEmail}`}>
                    <IconsLinear.Sms mr={2} fontSize={'sm'} />
                    {staticData.navigation.customerServiceEmail}
                </a>
            </Box>
            <HStack gap={4} mt={4}>
                <Box asChild fontWeight={'bold'}>
                    <a href={urlHelpers.getPhoneNoHref(staticData.navigation.hotline)}>
                        <IconsLinear.Call mr={2} fontSize={'sm'} />
                        {staticData.navigation.hotline}
                    </a>
                </Box>
                <Box as={'span'} fontSize={'xs'}>
                    {staticData.navigation.openingDays} {staticData.navigation.openingHours}
                    {' · '}
                    {staticData.navigation.openingDaysWeekend}{' '}
                    {staticData.navigation.openingHoursWeekend}
                </Box>
            </HStack>
        </Box>
    );
});

const MenuGroup = memo(({ navGroup }: { navGroup: NavGroupModel }): JSX.Element => {
    const [expandedMenuIndex, setExpandedMenuIndex] = useState<string | null>(null);
    const handleToggleMenu = (index: string) => {
        setExpandedMenuIndex(prevIndex => (prevIndex === index ? null : index));
    };
    const handleLogout = useCallback((isLogoutLink: boolean) => {
        if (isLogoutLink) {
            logOutUser();
        }
    }, []);

    return (
        <>
            <Box
                color={'gray.500'}
                textTransform={'uppercase'}
                pt={4}
                pb={2}
                fontWeight={'bold'}
                css={{
                    '&:first-of-type': {
                        pt: 0,
                    },
                }}
            >
                {navGroup.navGroupTitle}
            </Box>
            <VStack gap={0}>
                {navGroup.navLinks.map((navLink: NavLinkModel) => {
                    const isLogoutLink = navLink.navLinkId === '401';
                    return navLink.isExpandable ? (
                        <AccordionMenuItem
                            key={navLink.navLinkId}
                            index={navLink.navLinkId}
                            currentItemIndex={expandedMenuIndex}
                            navLink={navLink}
                            isOpen={expandedMenuIndex === navLink.navLinkId}
                            onMenuClick={() => handleToggleMenu(navLink.navLinkId)}
                        />
                    ) : (
                        <Link
                            key={navLink.navLinkId}
                            as={isLogoutLink ? Button : undefined}
                            href={!isLogoutLink ? modifyLink(navLink) : undefined}
                            variant={'menuLinkBase'}
                            mb={'3px'}
                            css={{
                                display: 'block',
                                bg: 'white',
                                fontSize: 'md',
                                py: 2.5,
                                fontWeight: navLink.navLinkId === '000' ? 'bold' : 'unset',
                                textAlign: 'start',
                                borderRadius: 'none',
                                maxH: isLogoutLink ? '2.75rem' : undefined,
                            }}
                            onClick={() => handleLogout(isLogoutLink)}
                        >
                            {navLink.navLinkLabel}
                        </Link>
                    );
                })}
            </VStack>
        </>
    );
});

export const NavigationMobile = ({
    menuLabel,
    closeLabel,
    user,
}: {
    menuLabel: string;
    closeLabel: string;
    user: User | null | undefined;
}) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'appHeader',
    });

    const [navOpen, setNavOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const showFull = useBreakpointValue({
        base: true,
        md: false,
    });

    const profileLinks = {
        navGroupId: 'userProfile',
        navGroupTitle: t('userMenu.groupHeader'),
        navLinks: [
            {
                navLinkId: '400',
                navLinkLabel: t('userMenu.goToPanel'),
                isExpandable: false,
                hasContentGroups: false,
                url: user ? getDashboardLink(user) : '',
            },
            {
                navLinkId: '401',
                navLinkLabel: t('userMenu.logOut'),
                isExpandable: false,
                hasContentGroups: false,
                url: '',
            },
        ],
    };

    if (user?.type === User.type.HOTELIER) {
        const addAccommodationLink = {
            navLinkId: '402',
            navLinkLabel: t('cta.addAccommodation'),
            isExpandable: false,
            hasContentGroups: false,
            url: (navLinks as NavGroupModel[])[0].navLinks[1].url ?? '/',
        };
        profileLinks.navLinks.push(addAccommodationLink);
    }

    const userNavLinks = navLinks.filter(
        (navGroup: NavGroupModel) => navGroup.navGroupId !== 'profile',
    );
    userNavLinks.push(profileLinks);

    const renderUserNavLinks = useMemo(
        () =>
            userNavLinks.map((navGroup: NavGroupModel) => (
                <MenuGroup navGroup={navGroup} key={navGroup.navGroupId} />
            )),
        [userNavLinks],
    );

    const renderDrawerContent = useMemo(
        () =>
            user
                ? renderUserNavLinks
                : navLinks.map((navGroup: NavGroupModel) => (
                      <MenuGroup navGroup={navGroup} key={navGroup.navGroupId} />
                  )),
        [user, renderUserNavLinks],
    );

    const handleNavigationClick = () => {
        if (!navOpen) {
            buttonRef.current?.scrollIntoView();
        }

        setNavOpen(!navOpen);
    };

    return (
        <>
            {!navOpen ? <Box hidden>{renderDrawerContent}</Box> : null}
            <Drawer.Root
                open={navOpen}
                onOpenChange={_ => setTimeout(() => handleNavigationClick(), 0)}
                placement={'start'}
                size={showFull ? 'full' : 'sm'}
                variant={'navigation'}
            >
                <Drawer.Trigger asChild>
                    <IconButton
                        ref={buttonRef}
                        aria-label={navOpen ? closeLabel : menuLabel}
                        ml={-4}
                        zIndex={navOpen ? 'popover' : 1}
                        css={{
                            '& svg': {
                                fontSize: '1.5rem',
                            },
                        }}
                        variant={'plain'}
                        px={3.5}
                    >
                        {navOpen ? (
                            <IconsLinear.Close color={'white'} />
                        ) : (
                            <IconsLinear.Menu color={'black'} />
                        )}
                    </IconButton>
                </Drawer.Trigger>

                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content>
                            <Drawer.Body as={'nav'} pb={28} px={2.5} minH={'100%'}>
                                {user
                                    ? renderUserNavLinks
                                    : navLinks.map((navGroup: NavGroupModel) => (
                                          <MenuGroup
                                              navGroup={navGroup}
                                              key={navGroup.navGroupId}
                                          />
                                      ))}
                                <MenuFooter />
                            </Drawer.Body>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </>
    );
};
